import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';

import { catchError, filter, map, of, switchMap, take, withLatestFrom } from 'rxjs';

import * as DataAccessExhibitorsActions from './data-access-exhibitors.actions';
import * as DataAccessExhibitorsReducer from './data-access-exhibitors.reducer';
import * as DataAccessExhibitorsSelectors from './data-access-exhibitors.selectors';

import { ExhibitorSearchSortOrder, ExhibitorSearchType } from '../../../interfaces/exhibitor.interfaces';
import { DataAccessEventsService } from '../../../services/events/data-access-events.service';
import { DataAccessExhibitorService } from '../../../services/exhibitors/data-access-exhibitors.service';
import { DataAccessPostsService } from '../../../services/posts/data-access-posts.service';
import * as DataAccessEventsActions from '../../events/entity/data-access-events.actions';
import { DataAccessFairsFacade } from '../../fairs/entity/data-access-fairs.facade';
import * as DataAccessPostsActions from '../../posts/entity/data-access-posts.actions';
import * as DataAccessProductsActions from '../../products/entity/data-access-products.actions';

@Injectable()
export class DataAccessExhibitorsEffects {
  searchResultsPerChunk = 100;

  loadExhibitor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorOpened, DataAccessExhibitorsActions.exhibitorContactPersonOpened),
      withLatestFrom(this.store.pipe(select(DataAccessExhibitorsSelectors.getExhibitor)), this.fairsFacade.fairId$),
      filter(([_, selectedExhibitor, __]) => !selectedExhibitor),
      switchMap(([action, _, fairId]) =>
        this.exhibitorService.getExhibitor(fairId, action.exhibitorId).pipe(
          map((exhibitor) => {
            return DataAccessExhibitorsActions.exhibitorLoadSucceeded({
              exhibitor: exhibitor,
              contactPersonIndex:
                typeof action.contactPersonIndex !== 'undefined' ? action.contactPersonIndex : undefined,
              fairId: fairId,
            });
          }),
          catchError((err) =>
            of(DataAccessExhibitorsActions.exhibitorLoadFailed({ error: err.status.toString(), fairId: fairId }))
          )
        )
      )
    )
  );

  loadParentExhibitor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorLoadSucceeded),
      filter((action) => !!action.exhibitor.parent_exhibitor_id),
      withLatestFrom(
        this.store.pipe(select(DataAccessExhibitorsSelectors.getParentExhibitor)),
        this.fairsFacade.fairId$
      ),
      filter(([_, parentExhibitor, __]) => !parentExhibitor),
      switchMap(([action, _, fairId]) =>
        this.exhibitorService.getExhibitor(fairId, action.exhibitor.parent_exhibitor_id ?? '').pipe(
          map((exhibitor) => {
            return DataAccessExhibitorsActions.exhibitorParentLoadSucceeded({
              exhibitor: exhibitor,
              fairId: fairId,
            });
          }),
          catchError((err) =>
            of(DataAccessExhibitorsActions.exhibitorLoadFailed({ error: err.status.toString(), fairId: fairId }))
          )
        )
      )
    )
  );

  loadCoExhibitorsWithPageview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorOpened),
      switchMap((_action) => {
        return this.store.pipe(
          select(DataAccessExhibitorsSelectors.getExhibitor),
          filter((exhibitor) => !!exhibitor && !exhibitor.parent_exhibitor_id),
          take(1)
        );
      }),
      withLatestFrom(this.store.pipe(select(DataAccessExhibitorsSelectors.getCoExhibitors)), this.fairsFacade.fairId$),
      filter(([_, coExhibitors, __]) => !coExhibitors),
      switchMap(([exhibitor, _, fairId]) => {
        return this.exhibitorService.getCoExhibitors(fairId, exhibitor!.id).pipe(
          map((exhibitors) => {
            return DataAccessExhibitorsActions.exhibitorCoExhibitorLoadSucceeded({
              exhibitor_id: exhibitor!.id,
              exhibitors: exhibitors,
              fairId: fairId,
            });
          }),
          catchError((err) =>
            of(DataAccessExhibitorsActions.exhibitorLoadFailed({ error: err.status.toString(), fairId: fairId }))
          )
        );
      })
    )
  );

  loadExhibitorPost$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessPostsActions.postLoadSucceeded),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(
              select(DataAccessExhibitorsSelectors.getExhibitorById, { exhibitorId: action.post.exhibitor_id })
            )
          )
        )
      ),
      // TODO: We need an option to check if a dataset is loaded as summary or detailed entry (also relevant for other data types)
      filter(
        ([_action, selectedExhibitor]) => !selectedExhibitor || typeof selectedExhibitor?.description === 'undefined'
      ),
      switchMap(([action, _selectedExhibitor]) =>
        this.exhibitorService.getExhibitor(action.post.fair_id, action.post.exhibitor_id).pipe(
          map((exhibitor) => {
            return DataAccessExhibitorsActions.exhibitorLoadSucceeded({
              exhibitor: exhibitor,
              fairId: action.post.fair_id,
            });
          }),
          catchError((err) =>
            of(
              DataAccessExhibitorsActions.exhibitorLoadFailed({
                error: err.status.toString(),
                fairId: action.post.fair_id,
              })
            )
          )
        )
      )
    )
  );

  loadExhibitorEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessEventsActions.eventLoadSucceeded),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(
              select(DataAccessExhibitorsSelectors.getExhibitorById, { exhibitorId: action.event.exhibitor_id })
            )
          )
        )
      ),
      // TODO: We need an option to check if a dataset is loaded as summary or detailed entry (also relevant for other data types)
      filter(
        ([_action, selectedExhibitor]) => !selectedExhibitor || typeof selectedExhibitor?.description === 'undefined'
      ),
      switchMap(([action, _selectedExhibitor]) =>
        this.exhibitorService.getExhibitor(action.event.fair_id, action.event.exhibitor_id).pipe(
          map((exhibitor) => {
            return DataAccessExhibitorsActions.exhibitorLoadSucceeded({
              exhibitor: exhibitor,
              fairId: action.event.fair_id,
            });
          }),
          catchError((err) =>
            of(
              DataAccessExhibitorsActions.exhibitorLoadFailed({
                error: err.status.toString(),
                fairId: action.event.fair_id,
              })
            )
          )
        )
      )
    )
  );

  loadExhibitorProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessProductsActions.productLoadSucceeded),
      filter((action) => !!action?.product.exhibitor_id),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(
              select(DataAccessExhibitorsSelectors.getExhibitorById, { exhibitorId: action.product.exhibitor_id })
            )
          )
        )
      ),
      // TODO: We need an option to check if a dataset is loaded as summary or detailed entry (also relevant for other data types)
      filter(
        ([_action, selectedExhibitor]) => !selectedExhibitor || typeof selectedExhibitor?.description === 'undefined'
      ),
      switchMap(([action, _selectedExhibitor]) =>
        this.fairsFacade.fairId$.pipe(
          switchMap((fairId) =>
            this.exhibitorService.getExhibitor(fairId, action.product.exhibitor_id).pipe(
              map((exhibitor) => {
                return DataAccessExhibitorsActions.exhibitorLoadSucceeded({
                  exhibitor: exhibitor,
                  fairId: fairId,
                });
              }),
              catchError((err) =>
                of(
                  DataAccessExhibitorsActions.exhibitorLoadFailed({
                    error: err.status.toString(),
                    fairId: fairId,
                  })
                )
              )
            )
          )
        )
      )
    )
  );

  loadExhibitorProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorFairTabOpened),
      withLatestFrom(this.fairsFacade.fairId$),
      switchMap(([action, fairId]) =>
        this.exhibitorService.getExhibitorProducts(fairId, action.exhibitorId).pipe(
          map((exhibitorProductsResponse) =>
            DataAccessExhibitorsActions.exhibitorProductsLoadSucceeded({
              exhibitorProductsResponse: exhibitorProductsResponse,
              exhibitorId: action.exhibitorId,
              fairId: action.fairId,
            })
          ),
          catchError((err) =>
            of(DataAccessExhibitorsActions.exhibitorProductsLoadFailed({ error: err.status.toString(), fairId }))
          )
        )
      )
    )
  );

  loadExhibitorProductsNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorProductsLoadNext),
      withLatestFrom(this.fairsFacade.fairId$),
      switchMap(([action, fairId]) =>
        this.exhibitorService
          .getExhibitorProducts(
            fairId,
            action.exhibitorId,
            action.exhibitorProductsResponse.cursors?.next ? action.exhibitorProductsResponse.cursors?.next : ''
          )
          .pipe(
            map((exhibitorProductsResponse) =>
              DataAccessExhibitorsActions.exhibitorProductsLoadSucceeded({
                exhibitorProductsResponse: exhibitorProductsResponse,
                exhibitorId: action.exhibitorId,
                fairId: action.fairId,
              })
            ),
            catchError((err) =>
              of(
                DataAccessExhibitorsActions.exhibitorProductsLoadFailed({
                  error: err.status.toString(),
                  fairId: action.fairId,
                })
              )
            )
          )
      )
    )
  );

  loadExhibitorPosts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorFairTabOpened),
      switchMap((action) =>
        this.postService.getPosts(action.fairId, undefined, undefined, action.exhibitorId).pipe(
          map((exhibitorPostsResponse) =>
            DataAccessExhibitorsActions.exhibitorPostsLoadSucceeded({
              exhibitorPostsResponse: exhibitorPostsResponse,
              exhibitorId: action.exhibitorId,
              fairId: action.fairId,
            })
          ),
          catchError((err) =>
            of(
              DataAccessExhibitorsActions.exhibitorPostsLoadFailed({
                error: err.status.toString(),
                fairId: action.fairId,
              })
            )
          )
        )
      )
    )
  );

  loadExhibitorPostsNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorPostsLoadNext),
      switchMap((action) =>
        this.postService
          .getPosts(
            action.fairId,
            action.exhibitorPostsResponse.cursors?.next ? action.exhibitorPostsResponse.cursors?.next : '',
            undefined,
            action.exhibitorId
          )
          .pipe(
            map((exhibitorPostsResponse) =>
              DataAccessExhibitorsActions.exhibitorPostsLoadSucceeded({
                exhibitorPostsResponse: exhibitorPostsResponse,
                exhibitorId: action.exhibitorId,
                fairId: action.fairId,
              })
            ),
            catchError((err) =>
              of(
                DataAccessExhibitorsActions.exhibitorPostsLoadFailed({
                  error: err.status.toString(),
                  fairId: action.fairId,
                })
              )
            )
          )
      )
    )
  );

  loadExhibitorEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorFairTabOpened),
      withLatestFrom(this.fairsFacade.fairId$),
      switchMap(([action, fairId]) =>
        this.eventService.getEvents(fairId, undefined, undefined, action.exhibitorId).pipe(
          map((exhibitorEventsResponse) =>
            DataAccessExhibitorsActions.exhibitorEventsLoadSucceeded({
              exhibitorEventsResponse: exhibitorEventsResponse,
              exhibitorId: action.exhibitorId,
              fairId: action.fairId,
            })
          ),
          catchError((err) =>
            of(
              DataAccessExhibitorsActions.exhibitorEventsLoadFailed({
                error: err.status.toString(),
                fairId: action.fairId,
              })
            )
          )
        )
      )
    )
  );

  loadExhibitorEventList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessEventsActions.eventLoadSucceeded),
      withLatestFrom(this.fairsFacade.fairId$),
      switchMap(([action, fairId]) =>
        this.eventService.getEvents(fairId, undefined, undefined, action.event.exhibitor_id).pipe(
          map((exhibitorEventsResponse) =>
            DataAccessExhibitorsActions.exhibitorEventsLoadSucceeded({
              exhibitorEventsResponse: exhibitorEventsResponse,
              exhibitorId: action.event.exhibitor_id,
              fairId: action.event.fair_id,
            })
          ),
          catchError((err) =>
            of(
              DataAccessExhibitorsActions.exhibitorEventsLoadFailed({
                error: err.status.toString(),
                fairId: action.event.fair_id,
              })
            )
          )
        )
      )
    )
  );

  loadExhibitorEventsNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorEventsLoadNext),
      withLatestFrom(this.fairsFacade.fairId$),
      switchMap(([action, fairId]) =>
        this.eventService
          .getEvents(
            fairId,
            action.exhibitorEventsResponse.cursors?.next ? action.exhibitorEventsResponse.cursors?.next : '',
            undefined,
            action.exhibitorId
          )
          .pipe(
            map((exhibitorEventsResponse) =>
              DataAccessExhibitorsActions.exhibitorEventsLoadSucceeded({
                exhibitorEventsResponse: exhibitorEventsResponse,
                exhibitorId: action.exhibitorId,
                fairId: action.fairId,
              })
            ),
            catchError((err) =>
              of(
                DataAccessExhibitorsActions.exhibitorEventsLoadFailed({
                  error: err.status.toString(),
                  fairId: action.fairId,
                })
              )
            )
          )
      )
    )
  );

  directoryOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorDirectoryOpened),
      withLatestFrom(
        this.store.pipe(select(DataAccessExhibitorsSelectors.getExhibitorDirectoryStatus)),
        this.fairsFacade.fairId$
      ),
      filter(([action, directoryStatus]) => !directoryStatus.loaded.letters.includes(action.letter)),
      switchMap(([action, _, fairId]) =>
        this.exhibitorService
          .getExhibitorDirectoryResults(
            {
              searchType: [ExhibitorSearchType.exhibitor, ExhibitorSearchType.brand],
              searchSort: ExhibitorSearchSortOrder.alphabeticalAsc,
              stringFilter: [
                {
                  name: 'nameInitial',
                  filters: [action.letter],
                },
              ],
            },
            fairId,
            0,
            this.searchResultsPerChunk
          )
          .pipe(
            map((response) => {
              let count = 0;
              if (response.counts.exhibitors) {
                count += response.counts.exhibitors;
              }
              if (response.counts.brands) {
                count += response.counts.brands;
              }

              if (count > response.results.length) {
                return DataAccessExhibitorsActions.exhibitorDirectoryLoadNext({
                  letter: action.letter,
                  loaded: response.results.length,
                  results: response.results,
                  count: count,
                  fairId: action.fairId,
                });
              }

              return DataAccessExhibitorsActions.exhibitorDirectoryLoadSucceeded({
                letter: action.letter,
                loaded: response.results.length,
                results: response.results,
                count: count,
                fairId: action.fairId,
              });
            }),
            catchError((err) =>
              of(
                DataAccessExhibitorsActions.exhibitorDirectoryLoadFailed({
                  error: err.status.toString(),
                  fairId: action.fairId,
                })
              )
            )
          )
      )
    )
  );

  directoryNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorDirectoryLoadNext),
      withLatestFrom(
        this.store.pipe(select(DataAccessExhibitorsSelectors.getExhibitorDirectoryStatus)),
        this.fairsFacade.fairId$
      ),
      filter(([action, directoryStatus]) => !directoryStatus.loaded.letters.includes(action.letter)),
      switchMap(([action, _, fairId]) =>
        this.exhibitorService
          .getExhibitorDirectoryResults(
            {
              searchType: [ExhibitorSearchType.exhibitor, ExhibitorSearchType.brand],
              searchSort: ExhibitorSearchSortOrder.alphabeticalAsc,
              stringFilter: [
                {
                  name: 'nameInitial',
                  filters: [action.letter],
                },
              ],
            },
            fairId,
            action.loaded,
            this.searchResultsPerChunk
          )
          .pipe(
            map((response) => {
              if ((action.count ?? 0) > action.loaded + this.searchResultsPerChunk) {
                return DataAccessExhibitorsActions.exhibitorDirectoryLoadNext({
                  letter: action.letter,
                  loaded: action.loaded + response.results.length,
                  results: response.results,
                  count: action.count,
                  fairId: action.fairId,
                });
              }

              return DataAccessExhibitorsActions.exhibitorDirectoryLoadSucceeded({
                letter: action.letter,
                loaded: action.loaded + response.results.length,
                results: response.results,
                count: action.count,
                fairId: action.fairId,
              });
            }),
            catchError((err) =>
              of(
                DataAccessExhibitorsActions.exhibitorDirectoryLoadFailed({
                  error: err.status.toString(),
                  fairId: action.fairId,
                })
              )
            )
          )
      )
    )
  );

  directoryHallsInitOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DataAccessExhibitorsActions.exhibitorDirectoryHallsOpened,
        DataAccessExhibitorsActions.exhibitorDirectoryOpened,
        DataAccessExhibitorsActions.exhibitorDirectorySearchOpened
      ),
      withLatestFrom(
        this.store.pipe(select(DataAccessExhibitorsSelectors.getExhibitorDirectoryStatus)),
        this.fairsFacade.fairId$
      ),
      filter(([_action, directoryStatus]) => !directoryStatus.loaded.availableHalls.length),
      switchMap(([action, _, fairId]) =>
        this.exhibitorService
          .getExhibitorDirectoryResults(
            {
              searchType: [ExhibitorSearchType.exhibitor, ExhibitorSearchType.brand],
              searchSort: ExhibitorSearchSortOrder.alphabeticalAsc,
            },
            fairId,
            0,
            this.searchResultsPerChunk
          )
          .pipe(
            map((response) => {
              const halls =
                response.facets.string_facets
                  .find((facet) => facet.name === 'exhibitionHall')
                  ?.types.map((hall: any) => hall.name) ?? [];

              const regularHalls = halls
                .filter((hall: any) => hall.length === 2)
                .sort((a: any, b: any) => a.localeCompare(b));

              const combinedHalls = [
                ...regularHalls,
                ...halls.filter((hall: any) => hall.length != 2).sort((a: any, b: any) => a.localeCompare(b)),
              ];

              return DataAccessExhibitorsActions.exhibitorDirectoryHallsInitSucceeded({
                halls: combinedHalls,
                fairId: action.fairId,
              });
            }),
            catchError((err) =>
              of(
                DataAccessExhibitorsActions.exhibitorDirectoryLoadFailed({
                  error: err.status.toString(),
                  fairId: action.fairId,
                })
              )
            )
          )
      )
    )
  );

  directoryHallOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorDirectoryHallsOpened),
      withLatestFrom(
        this.store.pipe(select(DataAccessExhibitorsSelectors.getExhibitorDirectoryStatus)),
        this.fairsFacade.fairId$
      ),
      filter(([action, directoryStatus]) => !directoryStatus.loaded.halls.includes(action.hall)),
      switchMap(([action, _, fairId]) =>
        this.exhibitorService
          .getExhibitorDirectoryResults(
            {
              searchType: [ExhibitorSearchType.exhibitor, ExhibitorSearchType.brand],
              searchSort: ExhibitorSearchSortOrder.alphabeticalAsc,
              stringFilter: [
                {
                  name: 'exhibitionHall',
                  filters: [action.hall],
                },
              ],
            },
            fairId,
            0,
            this.searchResultsPerChunk
          )
          .pipe(
            map((response) => {
              let count = 0;
              if (response.counts.exhibitors) {
                count += response.counts.exhibitors;
              }
              if (response.counts.brands) {
                count += response.counts.brands;
              }

              if (count > response.results.length) {
                return DataAccessExhibitorsActions.exhibitorDirectoryHallLoadNext({
                  hall: action.hall,
                  loaded: response.results.length,
                  results: response.results,
                  count: count,
                  fairId: action.fairId,
                });
              }

              return DataAccessExhibitorsActions.exhibitorDirectoryHallLoadSucceeded({
                hall: action.hall,
                loaded: response.results.length,
                results: response.results,
                count: count,
                fairId: action.fairId,
              });
            }),
            catchError((err) =>
              of(
                DataAccessExhibitorsActions.exhibitorDirectoryLoadFailed({
                  error: err.status.toString(),
                  fairId: action.fairId,
                })
              )
            )
          )
      )
    )
  );

  directoryHallNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorDirectoryHallLoadNext),
      withLatestFrom(
        this.store.pipe(select(DataAccessExhibitorsSelectors.getExhibitorDirectoryStatus)),
        this.fairsFacade.fairId$
      ),
      filter(([action, directoryStatus]) => !directoryStatus.loaded.halls.includes(action.hall)),
      switchMap(([action, _, fairId]) =>
        this.exhibitorService
          .getExhibitorDirectoryResults(
            {
              searchType: [ExhibitorSearchType.exhibitor, ExhibitorSearchType.brand],
              searchSort: ExhibitorSearchSortOrder.alphabeticalAsc,
              stringFilter: [
                {
                  name: 'exhibitionHall',
                  filters: [action.hall],
                },
              ],
            },
            fairId,
            action.loaded,
            this.searchResultsPerChunk
          )
          .pipe(
            map((response) => {
              if ((action.count ?? 0) > action.loaded + this.searchResultsPerChunk) {
                return DataAccessExhibitorsActions.exhibitorDirectoryHallLoadNext({
                  hall: action.hall,
                  loaded: action.loaded + response.results.length,
                  results: response.results,
                  count: action.count,
                  fairId: action.fairId,
                });
              }

              return DataAccessExhibitorsActions.exhibitorDirectoryHallLoadSucceeded({
                hall: action.hall,
                loaded: action.loaded + response.results.length,
                results: response.results,
                count: action.count,
                fairId: action.fairId,
              });
            }),
            catchError((err) =>
              of(
                DataAccessExhibitorsActions.exhibitorDirectoryLoadFailed({
                  error: err.status.toString(),
                  fairId: action.fairId,
                })
              )
            )
          )
      )
    )
  );

  directorySearchOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorDirectorySearchOpened),
      withLatestFrom(
        this.store.pipe(select(DataAccessExhibitorsSelectors.getExhibitorDirectoryStatus)),
        this.fairsFacade.fairId$
      ),
      switchMap(([action, _directoryStatus, fairId]) =>
        this.exhibitorService
          .getDisplayNameSearchResults(
            {
              searchTerm: action.searchTerm,
              searchType: [ExhibitorSearchType.exhibitor, ExhibitorSearchType.brand],
              searchSort: ExhibitorSearchSortOrder.alphabeticalAsc,
            },
            fairId
          )
          .pipe(
            map((response) => {
              let count = 0;
              if (response.counts.exhibitors) {
                count += response.counts.exhibitors;
              }
              if (response.counts.brands) {
                count += response.counts.brands;
              }

              return DataAccessExhibitorsActions.exhibitorDirectorySearchLoadSucceeded({
                searchTerm: action.searchTerm,
                results: response.results,
                count,
                fairId,
              });
            })
          )
      )
    )
  );

  directorySearchNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessExhibitorsActions.exhibitorDirectorySearchLoadNext),
      withLatestFrom(
        this.store.pipe(select(DataAccessExhibitorsSelectors.getExhibitorDirectoryStatus)),
        this.fairsFacade.fairId$
      ),
      switchMap(([action, directoryStatus, fairId]) =>
        this.exhibitorService
          .getDisplayNameSearchResults(
            {
              searchTerm: action.searchTerm,
              searchType: [ExhibitorSearchType.exhibitor, ExhibitorSearchType.brand],
              searchSort: ExhibitorSearchSortOrder.alphabeticalAsc,
            },
            fairId,
            directoryStatus?.searchStatus?.loadedResults ?? 0
          )
          .pipe(
            map((response) => {
              let count = 0;
              if (response.counts.exhibitors) {
                count += response.counts.exhibitors;
              }
              if (response.counts.brands) {
                count += response.counts.brands;
              }

              return DataAccessExhibitorsActions.exhibitorDirectorySearchLoadNextSucceeded({
                searchTerm: action.searchTerm,
                results: response.results,
                count,
                fairId,
              });
            }),
            catchError((err) =>
              of(DataAccessExhibitorsActions.exhibitorDirectoryLoadFailed({ error: err.status.toString(), fairId }))
            )
          )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store<DataAccessExhibitorsReducer.ExhibitorsState>,
    private exhibitorService: DataAccessExhibitorService,
    private postService: DataAccessPostsService,
    private eventService: DataAccessEventsService,
    private fairsFacade: DataAccessFairsFacade
  ) {}
}
